import styled, { createGlobalStyle } from 'styled-components';

// Color palette
export const colors = {
  // Primary colors
  black: '#000000',
  white: '#FFFFFF',
  green: '#00FF41', // Bright green for main accents
  lightGreen: '#00FF41', // Same as green for consistency
  
  // Grey scale - darker theme to match the image
  grey: {
    50: '#F9FAFB',
    100: '#F3F4F6',
    200: '#E5E7EB',
    300: '#D1D5DB',
    400: '#9CA3AF',
    500: '#6B7280',
    600: '#4B5563',
    700: '#252525',
    800: '#000000',
    900: '#111827',
    950: '#252525', // Even darker for cards
  },
  
  // Status colors - updated to match image
  success: '#00FF41', // Use the same green for success states
  warning: '#F59E0B',
  error: '#EF4444',
  danger: '#EF4444',
  info: '#3B82F6',
  
  // Position colors
  position: {
    QB: '#8B5CF6',
    RB: '#06B6D4',
    WR: '#10B981',
    TE: '#F59E0B',
    K: '#EF4444',
    DEF: '#6B7280',
  },
};

// Breakpoints
export const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1280px',
};

// Media queries
export const media = {
  mobile: `@media (min-width: ${breakpoints.mobile})`,
  tablet: `@media (min-width: ${breakpoints.tablet})`,
  desktop: `@media (min-width: ${breakpoints.desktop})`,
  wide: `@media (min-width: ${breakpoints.wide})`,
};

// Typography
export const typography = {
  fontFamily: {
    sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    mono: 'Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
  },
  fontSize: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  },
  lineHeight: {
    tight: 1.25,
    normal: 1.5,
    relaxed: 1.75,
  },
};

// Spacing
export const spacing = {
  px: '1px',
  0: '0',
  1: '0.25rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  8: '2rem',
  10: '2.5rem',
  12: '3rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
};

// Shadows
export const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  base: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
};

// Border radius
export const borderRadius = {
  none: '0',
  sm: '0.125rem',
  base: '0.25rem',
  md: '0.375rem',
  lg: '0.5rem',
  xl: '0.75rem',
  '2xl': '1rem',
  full: '9999px',
};

// Global styles
export const GlobalStyles = createGlobalStyle`
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  html {
    font-size: 16px;
    scroll-behavior: smooth;
    overflow-x: hidden;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 1rem;
    line-height: 1.5;
    color: #FFFFFF;
    background-color: #050505;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    overflow-x: hidden;
    padding-bottom: env(safe-area-inset-bottom);
  }

  #root {
    min-height: 100vh;
    overflow-x: hidden;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: ${typography.fontWeight.bold};
    line-height: ${typography.lineHeight.tight};
    margin-bottom: ${spacing[4]};
  }

  h1 {
    font-size: ${typography.fontSize['3xl']};
  }

  h2 {
    font-size: ${typography.fontSize['2xl']};
  }

  h3 {
    font-size: ${typography.fontSize.xl};
  }

  h4 {
    font-size: ${typography.fontSize.lg};
  }

  p {
    margin-bottom: ${spacing[4]};
  }

  a {
    color: ${colors.green};
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: ${colors.white};
    }
  }

  button {
    font-family: inherit;
    cursor: pointer;
    transition: all 0.2s ease;
    touch-action: manipulation;
  }

  button, a {
    -webkit-tap-highlight-color: rgba(255, 255, 255, 0.08);
  }

  input, textarea, select {
    font-family: inherit;
    font-size: 16px; /* Prevent zoom on iOS */
  }

  :focus {
    outline: 2px solid ${colors.green};
    outline-offset: 2px;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${colors.grey[900]};
  }

  ::-webkit-scrollbar-thumb {
    background: ${colors.grey[700]};
    border-radius: ${borderRadius.base};
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${colors.grey[600]};
  }

  /* Custom scrollbar for Firefox */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${colors.grey[700]} ${colors.grey[900]};
  }
`;

// Common styled components
export const Container = styled.div<{ maxWidth?: string; padding?: string }>`
  width: 100%;
  max-width: ${props => props.maxWidth || '1200px'};
  margin: 0 auto;
  padding: ${props => props.padding || `0 ${spacing[4]}`};

  ${media.tablet} {
    padding: ${props => props.padding || `0 ${spacing[6]}`};
  }

  ${media.desktop} {
    padding: ${props => props.padding || `0 ${spacing[8]}`};
  }
`;

export const Card = styled.div<{ 
  padding?: string; 
  margin?: string;
  background?: string;
  border?: string;
}>`
  background: #1c1c1c;
  border: 1px solid #000000;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin: 0;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.2s ease;
  overflow-wrap: anywhere;

  &:hover {
    box-shadow: ${shadows.md};
  }
`;

export const Button = styled.button.withConfig({
  shouldForwardProp: (prop) => !['fullWidth', 'variant', 'size'].includes(prop)
})<{
  variant?: 'primary' | 'secondary' | 'danger' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  disabled?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[2]};
  border: none;
  border-radius: ${borderRadius.md};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.6 : 1};
  width: ${props => props.fullWidth ? '100%' : 'auto'};
  min-height: 44px; /* Minimum touch target size for mobile */

  /* Variants */
  ${props => {
    switch (props.variant) {
      case 'secondary':
        return `
          background: transparent;
          color: ${colors.white};
          border: 1px solid ${colors.grey[700]};
          
          &:hover:not(:disabled) {
            background: ${colors.grey[900]};
            border-color: ${colors.grey[600]};
          }
        `;
      case 'danger':
        return `
          background: ${colors.error};
          color: ${colors.white};
          
          &:hover:not(:disabled) {
            background: #DC2626;
          }
        `;
      case 'success':
        return `
          background: ${colors.success};
          color: ${colors.white};
          
          &:hover:not(:disabled) {
            background: #059669;
          }
        `;
      default: // primary
        return `
          background: ${colors.green};
          color: ${colors.black};
          
          &:hover:not(:disabled) {
            background: ${colors.white};
          }
        `;
    }
  }}

  /* Sizes */
  ${props => {
    switch (props.size) {
      case 'sm':
        return `
          padding: ${spacing[2]} ${spacing[3]};
          font-size: ${typography.fontSize.sm};
          min-height: 36px;
        `;
      case 'lg':
        return `
          padding: ${spacing[4]} ${spacing[6]};
          font-size: ${typography.fontSize.lg};
          min-height: 52px;
        `;
      default: // md
        return `
          padding: ${spacing[3]} ${spacing[4]};
          font-size: ${typography.fontSize.base};
          min-height: 44px;
        `;
    }
  }}
`;

export const Input = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'error'
})<{ error?: boolean }>`
  flex: 1;
  background: #101111;
  border: 1px solid #4B5563;
  color: #FFFFFF;
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.base};
  min-height: 44px; /* Minimum touch target size for mobile */
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.green};
  }

  &::placeholder {
    color: ${colors.grey[500]};
  }
`;

export const Label = styled.label`
  display: block;
  margin-bottom: ${spacing[2]};
  font-weight: ${typography.fontWeight.medium};
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[300]};
`;

export const ErrorMessage = styled.span`
  display: block;
  margin-top: ${spacing[1]};
  font-size: ${typography.fontSize.sm};
  color: ${colors.error};
`;

export const LoadingSpinner = styled.div<{ size?: 'sm' | 'md' | 'lg' }>`
  width: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '32px';
      default: return '24px';
    }
  }};
  height: ${props => {
    switch (props.size) {
      case 'sm': return '16px';
      case 'lg': return '32px';
      default: return '24px';
    }
  }};
  border: 2px solid ${colors.grey[600]};
  border-top: 2px solid ${colors.green};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default GlobalStyles;
